import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  DmBadge,
  DmIcon,
  DmSidebar,
  DmSidebarCollapsible,
  DmSidebarContent,
  DmSidebarFooter,
  DmSidebarGroup,
  DmSidebarGroupContent,
  DmSidebarGroupLabel,
  DmSidebarHeader,
  DmSidebarMenu,
  DmSidebarMenuButton,
  DmSidebarMenuItem,
  DmSidebarMenuSubButton,
  DmSidebarMenuSubItem,
  DmSidebarTrigger,
  DmTooltip,
} from '@digitale-menschen/component-library-angular';
import {
  AccountMenuComponent,
  HasPermissionsForRoutePipe,
  LexplusLogoComponent,
  LOCALIZED_SHARED_ROUTES,
  LocalizedRouteService,
  ProfileState,
  SHARED_ROUTE_IDS,
} from '@lexplus-shared-lib';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '@lexplus-authentication';
import { ImpersonationSelectorComponent } from '../impersonation-selector/impersonation-selector.component';
import { UserPathPipe } from '../../pipes/user-path.pipe';
import { aufgabenControllerGetBadgeCounterForUserV1 } from '@lexplus-user-api';
import { HttpClient } from '@angular/common/http';
import { API_CONFIGURATION } from '@lexplus-environments';
import { lastValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';

@Component({
  selector: 'lp-sidebar-navigation',
  imports: [
    LexplusLogoComponent,
    AccountMenuComponent,
    RouterLink,
    RouterLinkActive,
    DmSidebar,
    DmSidebarHeader,
    DmSidebarContent,
    DmSidebarFooter,
    DmSidebarGroup,
    DmSidebarGroupLabel,
    DmSidebarGroupContent,
    DmSidebarMenu,
    DmSidebarMenuItem,
    DmSidebarMenuButton,
    DmBadge,
    DmSidebarCollapsible,
    DmSidebarMenuSubItem,
    DmSidebarMenuSubButton,
    DmSidebarTrigger,
    DmTooltip,
    DmIcon,
    HasPermissionsForRoutePipe,
    UserPathPipe,
    ImpersonationSelectorComponent,
  ],
  templateUrl: './sidebar-navigation.component.html',
  styleUrl: './sidebar-navigation.component.scss',
})
export class SidebarNavigationComponent implements OnInit {
  private readonly store = inject(Store);
  protected readonly isKundeDokumentManagerAktiv = this.store.selectSignal(ProfileState.isKundeDokumentenManagerAktiv);
  private readonly localizedRouteService = inject(LocalizedRouteService);
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  public readonly collapsed = input<boolean>(false);
  public readonly faqOpen = input<boolean>(false);
  public readonly faqRequested = output<void>();
  public readonly kontaktOpen = input<boolean>(false);
  public readonly kontaktRequested = output<void>();

  protected readonly aufgabenBadgeCounter = signal<number>(0);

  public ngOnInit(): void {
    void this.loadBadges();
  }

  protected sharedPath(routeId: keyof typeof SHARED_ROUTE_IDS): string {
    return `/${this.localizedRouteService.getLocalizedRoute(LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS[routeId]])}`;
  }

  protected logout(): void {
    const logoutPath = this.localizedRouteService.getLocalizedRoute(
      LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT],
      LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGOUT],
    );
    void this.router.navigateByUrl(`/${logoutPath}`);
  }

  private async loadBadges(): Promise<void> {
    try {
      const aufgabenCounterResult = await lastValueFrom(
        aufgabenControllerGetBadgeCounterForUserV1(this.httpClient, this.apiConfiguration.baseUrlUser, {}),
      );
      this.aufgabenBadgeCounter.set(aufgabenCounterResult.body.badgeCount);
    } catch {
      // The badge is decorative; a failed counter must not break the navigation.
    }
  }
}
